import React, { useState, useCallback, useEffect } from 'react'
import CustomDialog from 'sharedComponents/customDialog'
import useStyles from './style'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { Grid, TextField, Typography } from '@mui/material'
import moment from 'moment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import appointmentApi from 'services/appointment'
import { showLoader, hideLoader } from 'redux/loader/actions'
import { useDispatch } from 'react-redux'
import { showSnackbar } from 'redux/snackbar/actions'
import { isTimeEligible , extractTimeRange, isToday } from 'utils'

const slotOptions = [
  {
    label: '10:00 AM - 11:00 AM',
    value: '10:00',
    active: false,
  },
  {
    label: '11:00 AM - 12:00 PM',
    value: '11:00',
    active: false,
  },
  {
    label: '12:00 PM - 01:00 PM',
    value: '12:00',
    active: false,
  },
  {
    label: '01:00 PM - 02:00 PM',
    value: '01:00',
    active: false,
  },
  {
    label: '02:00 PM - 03:00 PM',
    value: '02:00',
    active: false,
  },
  {
    label: '03:00 PM - 04:00 PM',
    value: '03:00',
    active: false,
  },
  {
    label: '04:00 PM - 05:00 PM',
    value: '04:00',
    active: false,
  },
  {
    label: '05:00 PM - 06:00 PM',
    value: '05:00',
    active: false,
  },
]

const AppointmentModal = (props) => {
  const { open, handleClose, title, btnLabel, productId } = props
  const classes = useStyles()
  const dispatch = useDispatch()
  const [timeSlotOptions, setTimeSlotOptions] = useState([])

  // Save appointment api
  const handleContactUs = (formValues) => {
    dispatch(showLoader('Loading please wait...'))
    const data = {
      name: formValues?.name,
      mobilenum: formValues?.mobileNo,
      email: formValues?.email,
      date: moment(formValues?.date).format('DD-MM-YYYY'),
      time: formValues?.slot,
      message: formValues?.message,
      product_id: productId,
    }

    const onSuccess = (res) => {
      dispatch(hideLoader())
      if (res?.data?.status?.toLowerCase() === 'true') {
        dispatch(
          showSnackbar({
            message: res?.data?.message || 'Request sent successfully',
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            variant: 'success',
          })
        )
        formik.resetForm()
        setTimeSlotOptions([])
        handleClose()
      }
    }
    const onFailure = (err) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || 'Failed to fetch data',
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          variant: 'error',
        })
      )
      console.log('save appointment Api', err)
    }
    appointmentApi.saveAvailableTimeSlot(data).then(onSuccess, onFailure)
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      mobileNo: '',
      email: '',
      date: '',
      slot: '',
      message: '',
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().trim().required('Name is required'),
      mobileNo: yup
        .string()
        .required('Mobile number is required')
        .matches(/^[0-9]+$/, 'Only Numbers are allowed')
        .max(10, `Max 10 digits are allowed`)
        .min(10, `Must be exactly 10 numbers`)
        .nullable(),
      email: yup.string().email('Enter a valid email address'),
      date: yup
        .date()
        .required('Please select a date')
        .min(moment().subtract(1, 'day'), 'Invalid data')
        .max(moment().add(3, 'month'), 'Invalid date'),
      slot: yup.string().required('Please select time slot'),
      message: yup.string().max(500, 'Maximum 500 characters are allowed'),
    }),
    onSubmit: handleContactUs,
  })

  //Get appointment time slots api
  const getTimeSlots = useCallback(() => {
    dispatch(showLoader('Loading please wait...'))
    const onSuccess = (res) => {
      dispatch(hideLoader())
      if (res?.data?.status?.toLowerCase() === 'true') {
        const addCheckedKeyArr = res?.data?.data?.results?.map((data) => ({
          ...data,
          checked: false,
        }))
        setTimeSlotOptions(addCheckedKeyArr)
      } else {
        setTimeSlotOptions([])
      }
    }
    const onFailure = (err) => {
      dispatch(hideLoader())
      console.log('Get appointment time slots Api', err)
    }
    appointmentApi.getAvailableTimeSlot().then(onSuccess, onFailure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkSlotEligibility = (date, startTime, endTime) => {
    if(!(date && startTime && endTime)){
      console.error("Missing 'checkSlotEligibility' params...!");
    }
    console.log(isToday(date));
    if(!isToday(date)) return true;

    return isTimeEligible(startTime, endTime)
  }

  useEffect(() => {
    setTimeSlotOptions((prev) => ([...prev.map((data) => ({
      ...data,
      checked: false,
      isEligible: checkSlotEligibility(
        formik?.values?.date?._d,
        extractTimeRange(data?.time)?.startTime,
        extractTimeRange(data?.time)?.endTime
      ),
    }))]));
    formik.setFieldValue('slot', "")
  }, [formik?.values?.date])

  useEffect(() => {
    open && getTimeSlots()
  }, [getTimeSlots, open])

  // Set active time slot and selected time
  const handleTimeSlot = (slot) => {
    const updatedTimeArr = timeSlotOptions?.map((data) =>
      slot?._id === data?._id
        ? { ...data, checked: true }
        : { ...data, checked: false }
    )
    formik.setFieldValue('slot', slot?.time)
    setTimeSlotOptions(updatedTimeArr)
  }

  return (
    <>
      <CustomDialog
        open={open}
        handleClose={() => {
          handleClose()
          formik.resetForm()
        }}
        title={title}
        btnLabel={btnLabel}
        onSubmit={formik?.handleSubmit}
        customStyle={classes.dialogRoot}
      >
        <Grid container flexDirection={'column'} className={classes.root}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="name"
              id="name"
              value={formik?.values?.name}
              onChange={formik?.handleChange}
              type={'text'}
              fullWidth
              variant="outlined"
              label="Name*"
              size="small"
              error={formik?.errors?.name && formik?.touched?.name}
              helperText={
                formik?.errors?.name &&
                formik?.touched?.name &&
                formik?.errors?.name
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="mobileNo"
              id="mobileNo"
              value={formik?.values?.mobileNo}
              onChange={formik?.handleChange}
              type={'text'}
              fullWidth
              variant="outlined"
              label="Mobile No*"
              size="small"
              error={formik?.errors?.mobileNo && formik?.touched?.mobileNo}
              helperText={
                formik?.errors?.mobileNo &&
                formik?.touched?.mobileNo &&
                formik?.errors?.mobileNo
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="email"
              id="email"
              value={formik?.values?.email}
              onChange={formik?.handleChange}
              type={'text'}
              fullWidth
              variant="outlined"
              label="Email"
              size="small"
              error={formik?.errors?.email && formik?.touched?.email}
              helperText={
                formik?.errors?.email &&
                formik?.touched?.email &&
                formik?.errors?.email
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <MobileDatePicker
                label="Date*"
                value={formik?.values?.date}
                onChange={(value) => {
                  formik.setFieldValue('date', value)
                }}
                inputFormat="DD/MM/YYYY"
                format="DD/MM/YYYY"
                minDate={new Date()}
                maxDate={moment().add(3, 'month')}
                showTodayButton={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    error={formik.touched.date && formik.errors.date}
                    helperText={
                      formik.touched.date &&
                      formik.errors.date &&
                      formik.errors.date
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} className={classes.slotLabel}>
            <Typography variant="body1">Available Time Slots</Typography>
          </Grid>
          <Grid item xs={12}>
            <section className={classes.slotWrapper}>
              {timeSlotOptions?.map((data) => (
                <div
                  className={
                    data?.checked === true
                      ? `${classes.slot} ${classes.activeSlot}`
                      : !data?.isEligible
                      ? `${classes.slot} ${classes.disabledSlot}`
                      : classes.slot
                  }
                  key={data?._id}
                  onClick={() => handleTimeSlot(data)}
                >
                  <Typography variant="body2">{data?.time}</Typography>
                </div>
              ))}
            </section>
          </Grid>
          <Grid item xs={12} className={classes.error}>
            <Typography variant="subtitle1">
              {formik?.errors?.slot &&
                formik?.touched?.slot &&
                formik?.errors?.slot}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="message"
              id="message"
              value={formik?.values?.message}
              onChange={formik?.handleChange}
              type={'text'}
              fullWidth
              variant="outlined"
              label="Message"
              size="small"
              multiline
              rows={5}
              rowsMax={5}
              error={formik?.errors?.message && formik?.touched?.message}
              helperText={
                formik?.errors?.message &&
                formik?.touched?.message &&
                formik?.errors?.message
              }
            />
          </Grid>
        </Grid>
      </CustomDialog>
    </>
  )
}

export default AppointmentModal
